import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Phone, CheckCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { products } from "../data/mock";
import { CallbackForm } from "../components/CallbackForm";
import { ProductImage } from "@/components/ProductImage";

export default function ProductsPage() {
  const navigate = useNavigate();
  const [activeProduct, setActiveProduct] = useState(null);

  return (
    <main className="pt-20 lg:pt-24">
      {/* Page Header */}
      <section className="bg-[#0a2a43] py-16 lg:py-20 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#ff9900]/5 rounded-full blur-3xl" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-white/60 hover:text-[#ff9900] transition-colors duration-200 mb-6 text-sm font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </button>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
            Products Portfolio
          </h1>
          <p className="text-white/60 text-lg max-w-2xl">
            Explore our complete range of water level monitoring and automation
            solutions built for reliability and performance.
          </p>
        </div>
      </section>

      {/* Products List */}
      <section className="py-20 bg-[#f8fafc]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-12">
            {products.map((product, index) => {
              const isExpanded = activeProduct === product.id;

              return (
                <div
                  key={product.id}
                  className="bg-white rounded-3xl border border-[#e2e8f0] overflow-hidden hover:shadow-xl hover:shadow-[#0a2a43]/5 transition-all duration-500"
                >
                  <div className="grid lg:grid-cols-2 items-stretch">
                    {/* Image */}
                    <div
                      className={`relative h-64 lg:h-auto flex items-center justify-center ${index % 2 === 1 ? "lg:order-2" : ""
                        }`}
                    >
                      <ProductImage
                        publicId={product.image}
                        alt={product.title}
                        className="max-h-[250px] object-contain"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0a2a43]/30 to-transparent lg:bg-none" />
                      <div className="absolute top-5 left-5">
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-8 lg:p-12 flex flex-col justify-center">
                      <h2 className="text-2xl lg:text-3xl font-bold text-[#0a2a43] mb-4">
                        {product.title}
                      </h2>
                      <p className="text-[#4a6680] text-lg leading-relaxed mb-6">
                        {product.description}
                      </p>

                      {/* Features */}
                      <div className="space-y-3 mb-8">
                        <h4 className="text-sm font-semibold text-[#0a2a43] uppercase tracking-wider">
                          Key Features
                        </h4>
                        {product.features.map((feature) => (
                          <div
                            key={feature}
                            className="flex items-start gap-3"
                          >
                            <CheckCircle className="w-4 h-4 text-[#ff9900] mt-1 flex-shrink-0" />
                            <span className="text-[#4a6680] text-sm">
                              {feature}
                            </span>
                          </div>
                        ))}
                      </div>

                      {/* CTA */}
                      <div className="flex flex-wrap gap-3">
                        <Button
                          onClick={() =>
                            setActiveProduct(
                              isExpanded ? null : product.id
                            )
                          }
                          className="bg-[#ff9900] hover:bg-[#e68a00] text-white font-semibold px-6 py-5 rounded-xl shadow-md shadow-[#ff9900]/15"
                        >
                          <Phone className="w-4 h-4 mr-2" />
                          Request Callback
                        </Button>
                        <Button
                          onClick={() => {
                            const el = document.getElementById("contact");
                            if (el) {
                              el.scrollIntoView({ behavior: "smooth" });
                            } else {
                              navigate("/#contact");
                            }
                          }}
                          variant="outline"
                          className="border-[#e2e8f0] text-[#4a6680] hover:border-[#ff9900]/30 hover:text-[#ff9900] font-semibold px-6 py-5 rounded-xl"
                        >
                          Contact Us
                        </Button>
                      </div>

                      {/* Inline Callback Form */}
                      {isExpanded && (
                        <div className="mt-8 animate-in slide-in-from-top-2 duration-300">
                          <CallbackForm variant="inline" source="products-inline" />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </main>
  );
}
